import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["threadId"];
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isFailed, isLoading, isOutOfSync } from 'conversations-async-data/async-data/operators/statusComparators';
import { getData, getError } from 'conversations-async-data/async-data/operators/getters';
import ChatFilterOptions from 'conversations-internal-schema/constants/ChatFilterOptions';
import { isInSearchAndFilterMode as getIsInSearchAndFilterMode } from 'conversations-inbox-lib/thread-search-and-filtering/public/selectors';
import { selectFilteredThreadListMemberWithThreadId } from 'find-and-filter-data/search-and-filter-data/protected';
import { useContact } from '../hooks/useContact';
import { fetchCrmContactOwners } from '../actions/fetchCrmContactOwners';
import ContactSidebarPlaceholder from '../../unified-inbox/components/ContactSidebarPlaceholder';
import ContactSidebarAsyncError from './ContactSidebarAsyncError';
import ContactSidebar from './ContactSidebar';
import { getThreadListMemberByThreadId } from '../../thread-list-member/selectors/getThreadListMemberByThreadId';
import { getInitialThreadViewState } from 'conversations-inbox-lib/initialThreadView/public/selectors';
import { FocusedThreadIdProvider } from './FocusedThreadIdProvider';
import { getFilteredSource, getStatus } from 'find-and-filter-data/view-members-schema/protected';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * @description The ContextualSidebarContainer manages relevant contextual information to
 * display to the user based on the current conversation
 */

const ContextualSidebar = ({
  threadId
}) => {
  const dispatch = useDispatch();
  const {
    asyncContact,
    retryFailed
  } = useContact({
    threadId
  });
  const contact = getData(asyncContact);

  //Determine whether to pull the thread list member from the realtime
  //thread list/view or from the search and filtering results.
  //If it doesn't exist in either place, it's likely because the user
  //is deep linking to a thread that is too old to be included in the initial
  //page of thread list members, so check the omnibus response.
  const threadListMemberRealtime = useSelector(state => getThreadListMemberByThreadId(state, {
    threadId
  }));
  const threadListMemberFilterResult = useSelector(state => selectFilteredThreadListMemberWithThreadId(state, {
    threadId
  }));
  const threadViewStateFromOmnibus = useSelector(getInitialThreadViewState);
  const threadDetailsFromOmnibus = threadViewStateFromOmnibus ? threadViewStateFromOmnibus.threadDetails : undefined;
  const isInSearchAndFilterMode = useSelector(getIsInSearchAndFilterMode);
  const threadListMember = isInSearchAndFilterMode ? threadListMemberFilterResult : threadListMemberRealtime || threadDetailsFromOmnibus;

  //Determine if the provided thread is filtered as this disables some functionality.
  const isThreadSoftDeleted = getStatus(threadListMember) === ChatFilterOptions.SOFT_DELETED;
  const isThreadFiltered = getFilteredSource(threadListMember) !== null && getFilteredSource(threadListMember) !== 'NOT_FILTERED';
  useEffect(() => {
    dispatch(fetchCrmContactOwners({}));
  }, [dispatch]);
  const error = getError(asyncContact); // TODO: Remove cast
  const isObjectNotFound = error && error.message === 'OBJECT_NOT_FOUND';
  if (isFailed(asyncContact) && !isObjectNotFound) {
    return /*#__PURE__*/_jsx(ContactSidebarAsyncError, {
      retry: retryFailed
    });
  }
  if ((isOutOfSync(asyncContact) || isLoading(asyncContact)) && !contact) {
    return /*#__PURE__*/_jsx(ContactSidebarPlaceholder, {});
  }
  return /*#__PURE__*/_jsx(FocusedThreadIdProvider, {
    threadId: threadId,
    children: /*#__PURE__*/_jsx(ContactSidebar, {
      contact: contact,
      isThreadFiltered: isThreadFiltered,
      isThreadSoftDeleted: isThreadSoftDeleted,
      threadId: threadId
    })
  });
};
const ContextualSidebarContainer = /*#__PURE__*/memo(_ref => {
  let {
      threadId
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!threadId) {
    return /*#__PURE__*/_jsx(ContactSidebarPlaceholder, {});
  }
  return /*#__PURE__*/_jsx(ContextualSidebar, Object.assign({
    threadId: threadId
  }, props));
});
ContextualSidebarContainer.displayName = 'ContextualSidebarContainer';
ContextualSidebar.displayName = 'ContextualSidebar';
export default ContextualSidebarContainer;